import { useEffect, useState } from 'react';
import {
  ApiError,
  Avatar,
  Badge,
  Button,
  ChatLauncher,
  ErrorNote,
  MapLegend,
  RideMap,
  Sheet,
  Spinner,
  Stars,
  api,
  brl,
  haptic,
  km,
  minutes,
  phone as fmtPhone,
  rideStatusLabel,
  useGeolocation,
  useSocket,
} from '@mobi/web';
import { haversineMeters, isAtPickup, type LatLng } from '@mobi/shared';
import type { Ride } from '../useRide.js';

const SEARCHING = new Set(['requested', 'offering']);

/**
 * Antes de entrar no carro. Depois disso o passageiro está a bordo e se
 * afastar da origem é exatamente o que deve acontecer.
 */
const BEFORE_PICKUP = new Set(['requested', 'offering', 'accepted', 'driver_arrived']);

export function ActiveRide({
  ride,
  driverPosition,
  onChanged,
}: {
  ride: Ride;
  driverPosition: (LatLng & { heading?: number }) | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const geo = useGeolocation(true);
  const { socket } = useSocket(true);

  /**
   * Manda a própria posição para o motorista enquanto ele vem buscar.
   *
   * Para no embarque: depois que entrou no carro, os dois estão no mesmo
   * ponto e continuar transmitindo só gastaria bateria.
   */
  useEffect(() => {
    if (!socket || geo.status !== 'ok') return;
    if (ride.status !== 'accepted' && ride.status !== 'driver_arrived') return;

    const send = () => socket.emit('passenger:position', { lat: geo.coords.lat, lng: geo.coords.lng });
    send();
    const timer = setInterval(send, 4_000);
    return () => clearInterval(timer);
  }, [socket, geo, ride.status]);

  async function act(path: string, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/rides/${ride.id}/${path}`, { method: 'POST', body: {} });
      haptic();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível concluir');
    } finally {
      setBusy(false);
    }
  }

  async function share() {
    if (!ride.shareToken) return;
    const url = `${location.origin}/acompanhar/${ride.shareToken}`;
    const text = `Estou a caminho. Acompanhe minha viagem: ${url}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Minha viagem', text, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2_500);
      }
    } catch {
      // Usuário cancelou o compartilhamento. Não é erro.
    }
  }

  const searching = SEARCHING.has(ride.status);
  const driver = ride.driver;

  /**
   * Distância até o motorista, ao vivo. Só antes do embarque — depois
   * disso os dois estão no mesmo carro, e a distância seria sempre zero.
   */
  const distanceToDriverMeters =
    driverPosition && geo.status === 'ok' && BEFORE_PICKUP.has(ride.status)
      ? haversineMeters(geo.coords, driverPosition)
      : null;

  /*
   * O motorista está indo para o endereço marcado no pedido, não para
   * onde o passageiro andou depois. Mesmo raio que trava o "cheguei" do
   * motorista, para os dois lados concordarem sobre o que é "no ponto".
   */
  const strayedMeters =
    geo.status === 'ok' && BEFORE_PICKUP.has(ride.status) && !isAtPickup(geo.coords, ride.origin)
      ? Math.round(haversineMeters(geo.coords, ride.origin))
      : null;

  /*
   * O motorista marcou "cheguei" e está de fato no ponto. O servidor já
   * recusa marcar chegada de longe, mas ele pode ter dado a volta no
   * quarteirão depois — então confere a posição viva quando existe.
   *
   * Muda o pedido feito ao passageiro: enquanto o carro está a caminho,
   * ele deve ficar onde marcou; com o carro parado esperando, quem anda
   * é ele.
   */
  const driverWaiting =
    ride.status === 'driver_arrived' &&
    (driverPosition ? isAtPickup(driverPosition, ride.origin) : true);

  const walkPath = strayedMeters !== null && driverWaiting && geo.status === 'ok'
    ? [geo.coords, ride.origin]
    : null;

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        <RideMap
          className="absolute inset-0"
          routePolyline={ride.routePolyline}
          walkPath={walkPath}
          markers={[
            { point: ride.origin, kind: 'origin' },
            { point: ride.destination, kind: 'destination' },
            // Só enquanto ele precisa caminhar: fora disso, um ponto a
            // mais no mapa é ruído para quem está acompanhando o carro.
            ...(walkPath ? [{ point: walkPath[0]!, kind: 'passenger' as const }] : []),
            ...(driverPosition
              ? [
                  {
                    point: driverPosition,
                    kind: 'driver' as const,
                    vehicleType: driver?.vehicle?.type === 'moto' ? ('moto' as const) : ('car' as const),
                    id: driver?.id,
                    label: driver?.name,
                    photoUrl: driver?.hasPhoto ? `/api/rides/${ride.id}/photo/driver` : null,
                    ...(driverPosition.heading !== undefined
                      ? { heading: driverPosition.heading }
                      : {}),
                  },
                ]
              : []),
          ]}
        />
        <div className="pt-safe absolute inset-x-0 top-0 px-4">
          <div className="elevated-lg rounded-3xl border border-line/60 bg-surface/85 px-4 py-3 backdrop-blur-xl">
            {distanceToDriverMeters !== null && (
              <div className="mb-2.5 flex items-center justify-between border-b border-line/60 pb-2.5">
                <span className="eyebrow text-faint">
                  {driverWaiting ? 'Motorista esperando' : 'Motorista a caminho'}
                </span>
                <span className="tabular text-sm font-bold text-brand">
                  {km(distanceToDriverMeters)}
                </span>
              </div>
            )}
            <MapLegend
              items={[
                { kind: 'origin', label: 'Embarque' },
                { kind: 'destination', label: 'Destino' },
                ...(driverPosition ? [{ kind: 'driver' as const, label: 'Motorista' }] : []),
              ]}
            />
          </div>
        </div>
      </div>

      <Sheet className="max-h-[62%] overflow-y-auto">
        {strayedMeters !== null && (
          <div
            role="alert"
            className="mb-4 rounded-2xl bg-warn/15 px-4 py-3 text-sm text-warn"
          >
            {driverWaiting ? (
              <>
                <strong className="block">O motorista já está no ponto esperando.</strong>
                Você está a {strayedMeters} m dele. Siga o trajeto pontilhado no mapa até{' '}
                {ride.origin.address}.
              </>
            ) : (
              <>
                <strong className="block">Volte para o ponto de embarque.</strong>
                Você está a {strayedMeters} m de onde pediu a corrida. Aguarde ali — o
                motorista está indo para {ride.origin.address}.
              </>
            )}
          </div>
        )}

        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">{rideStatusLabel(ride.status)}</h2>
            <p className="tabular mt-1 text-sm text-muted">
              {km(ride.estimate.distanceMeters)} · {minutes(ride.estimate.durationSeconds)} ·{' '}
              {brl(ride.estimate.fareCents)}
            </p>
          </div>
          {searching && <Spinner className="mt-1 h-6 w-6 text-brand" />}
        </div>

        {searching && (
          <p className="mt-4 text-sm text-muted">
            Estamos oferecendo sua corrida aos motoristas mais próximos. Isso costuma levar menos
            de um minuto.
          </p>
        )}

        {driver && (
          <div className="mt-5 rounded-2xl bg-surface-2 p-4">
            <div className="flex items-center justify-between gap-3">
              <Avatar
                name={driver.name}
                src={driver.hasPhoto ? `/api/rides/${ride.id}/photo/driver` : null}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{driver.name}</p>
                <Stars value={driver.rating} count={driver.ratingCount} />
              </div>
              {driver.vehicle && (
                <div className="shrink-0 text-right">
                  <p className="font-mono text-sm font-bold tracking-wider">
                    {driver.vehicle.plate}
                  </p>
                  <p className="text-xs text-muted">
                    {driver.vehicle.model} · {driver.vehicle.color}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 flex gap-2">
              {driver.phone && (
                <a
                  href={`tel:${driver.phone}`}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-line font-semibold"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                    <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2Z" />
                  </svg>
                  Ligar {fmtPhone(driver.phone)}
                </a>
              )}
              {!searching && (
                <ChatLauncher
                  socket={socket}
                  rideId={ride.id}
                  otherName={driver.name}
                  buttonClassName="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-line text-ink"
                />
              )}
            </div>
          </div>
        )}

        {ride.status === 'driver_arrived' && (
          <div className="mt-4">
            <Badge tone="good">Seu motorista está no ponto de embarque</Badge>
          </div>
        )}

        {ride.status === 'in_progress' && (
          <p className="mt-4 text-sm text-muted">
            Boa viagem. No fim, você paga{' '}
            <strong className="text-ink">{brl(ride.estimate.fareCents)}</strong> direto ao
            motorista — menos o saldo da sua carteira, se houver.
          </p>
        )}

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-5 space-y-3">
          {ride.shareToken && (
            <Button variant="outline" full onClick={share}>
              {copied ? 'Link copiado' : 'Compartilhar minha viagem'}
            </Button>
          )}

          {ride.status !== 'in_progress' && (
            <Button
              variant="ghost"
              full
              loading={busy}
              onClick={() => act('cancel', 'Cancelar esta corrida?')}
            >
              Cancelar corrida
            </Button>
          )}
        </div>
      </Sheet>
    </div>
  );
}
